import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, ArrowUpRight, Linkedin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const OFFICES = [
  {
    label: "Corporate Office",
    city: "Bhubaneswar, Odisha",
    address: "HIG-1, A-13 Lingaraj Vihar, Pokhariput, Bhubaneswar, 751020",
  },
  {
    label: "Registered Office",
    city: "Balasore, Odisha",
    address: "Basanti Tower, Near HP Petrol Pump, Dudpal, Kamarda, Balasore, Odisha, 756035",
  },
  {
    label: "Branch Office",
    city: "Ghaziabad, UP",
    address: "Fortune Residency, Raj Nagar Extension, Ghaziabad, Uttar Pradesh, 201017",
  },
];

const DIVISION_OPTIONS = ["General", "Healthcare", "Solar / Renewable Energy", "Green Hydrogen", "EPC"];

const initialForm = { name: "", email: "", phone: "", division: "General", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will reach out shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "bg-navy-800/60 border-white/10 rounded-none h-12 text-slate-100 placeholder:text-slate-600 focus-visible:ring-electric focus-visible:border-electric/60";

  return (
    <section id="contact" className="py-28 lg:py-40 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
        >
          Contact
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-20 max-w-4xl"
          data-testid="contact-heading"
        >
          Let&apos;s build <span className="text-outline">what comes next.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2">Name</label>
                <Input name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" className={inputClass} data-testid="contact-name-input" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2">Email</label>
                <Input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@company.com" className={inputClass} data-testid="contact-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2">Phone</label>
                <Input name="phone" value={form.phone} onChange={handleChange} required placeholder="+91" className={inputClass} data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2">Division</label>
                <select
                  name="division"
                  value={form.division}
                  onChange={handleChange}
                  className="w-full h-12 bg-navy-800/60 border border-white/10 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-electric"
                  data-testid="contact-division-select"
                >
                  {DIVISION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt} className="bg-navy-800">{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-500 block mb-2">Message</label>
              <Textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                placeholder="Tell us about your project or requirement..."
                className="bg-navy-800/60 border-white/10 rounded-none min-h-36 text-slate-100 placeholder:text-slate-600 focus-visible:ring-electric focus-visible:border-electric/60"
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="group font-mono-accent text-xs tracking-[0.2em] uppercase bg-electric hover:bg-electric-hover disabled:opacity-60 text-white px-10 py-4 flex items-center gap-2 transition-colors duration-300"
              data-testid="contact-submit-button"
            >
              {submitting ? (
                <>
                  Sending <Loader2 className="w-4 h-4 animate-spin" />
                </>
              ) : (
                <>
                  Send Enquiry
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-10"
          >
            <div className="space-y-4">
              <a href="tel:+917205925473" className="flex items-center gap-4 text-slate-300 hover:text-neon transition-colors duration-300" data-testid="contact-phone-1">
                <Phone className="w-4 h-4 text-electric" /> +91 72059 25473
              </a>
              <a href="tel:+919937865473" className="flex items-center gap-4 text-slate-300 hover:text-neon transition-colors duration-300" data-testid="contact-phone-2">
                <Phone className="w-4 h-4 text-electric" /> +91 99378 65473
              </a>
              <a href="mailto:info.genoumgroup@gmail.com" className="flex items-center gap-4 text-slate-300 hover:text-neon transition-colors duration-300" data-testid="contact-email-link">
                <Mail className="w-4 h-4 text-electric" /> info.genoumgroup@gmail.com
              </a>
              <a
                href="https://www.linkedin.com/in/genoum-group-5aa346428"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-slate-300 hover:text-neon transition-colors duration-300"
                data-testid="contact-linkedin-link"
              >
                <Linkedin className="w-4 h-4 text-electric" /> linkedin.com/in/genoum-group
              </a>
            </div>

            <div className="space-y-5">
              {OFFICES.map((office, i) => (
                <div key={office.city} className="card-glow border border-white/10 bg-navy-800/40 p-6" data-testid={`office-card-${i}`}>
                  <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-neon">{office.label}</p>
                  <p className="font-display text-lg font-bold mt-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-electric" /> {office.city}
                  </p>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{office.address}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
